import { useState, useEffect } from "react";
import { roomTypes } from '../../data/shreeData';

const BookingSection = ({ isOpen, onClose, selectedRoom }) => {

  const isModal = isOpen !== undefined;

  const todayDate = new Date();
  const tomorrowDate = new Date();
  tomorrowDate.setDate(todayDate.getDate() + 1);
  const today = todayDate.toISOString().split("T")[0];
  const tomorrow = tomorrowDate.toISOString().split("T")[0];

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    checkIn: today,
    checkOut: tomorrow,
    roomId: roomTypes[0].id,
    extraGuests: 0,
    requests: "",
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (selectedRoom?.id) {
      setForm(f => ({ ...f, roomId: selectedRoom.id }));
    }
  }, [selectedRoom]);

  const selected = roomTypes.find((r) => r.id === form.roomId);

  const defaultGuests = selected?.maxGuests ?? 2;
  const totalGuests = defaultGuests + Number(form.extraGuests || 0);
  const pricePerNight = selected?.price || 0;

  const calcNights = () => {
    if (!form.checkIn || !form.checkOut) return 0;
    const diff = (new Date(form.checkOut) - new Date(form.checkIn)) / (1000 * 60 * 60 * 24);
    return diff > 0 ? diff : 0;
  };
  const nights = calcNights();
  const total = pricePerNight && nights ? pricePerNight * nights : null;

  useEffect(() => {
    if (!isModal) return;
    const handleKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isModal, onClose]);

  useEffect(() => {
    if (!isModal) return;
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen, isModal]);

  useEffect(() => {
    if (isModal && !isOpen) {
      setTimeout(() => {
        setSubmitted(false);
        setErrors({});
        setForm({
          name: "", email: "", phone: "",
          checkIn: today, checkOut: tomorrow,
          roomId: selectedRoom?.id ?? roomTypes[0].id,
          extraGuests: 0, requests: "",
        });
      }, 300);
    }
  }, [isOpen, isModal]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.phone.match(/^[0-9+\s-]{10,}$/)) e.phone = "Invalid phone";
    if (form.email && !form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      e.email = "Invalid email";
    if (form.checkIn && form.checkOut && form.checkOut <= form.checkIn)
      e.checkOut = "Must be after check-in";
    return e;
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});

    const amenitiesList = selected?.amenities?.join(", ") || "—";

    const message = `
🌊 *New Booking Inquiry*
  Shrileela Beach Resort

👤 Name: ${form.name}
📱 Phone: ${form.phone}
📧 Email: ${form.email || "—"}

📅 Check-in: ${form.checkIn}
📅 Check-out: ${form.checkOut}
🌙 Nights: ${nights}

🏨 Room: ${selected?.name}
🛎️ Amenities: ${amenitiesList}
👥 Max Guests: ${selected?.maxGuests}
💰 Price: ₹${pricePerNight}/night
💳 Estimated Total: ₹${total || "—"}

👥 Total Guests: ${totalGuests}

📝 Requests: ${form.requests || "None"}
    `.trim();

    window.open(
      `https://wa.me/9422801286?text=${encodeURIComponent(message)}`,
      "_blank"
    );
    setSubmitted(true);
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

    :root {
      --green-dark: #1a2b1a;
      --green-mid: #3d5a3e;
      --green-light: #5a7a5b;
      --green-pale: #c8dcc8;
      --green-bg: #f0f5f0;
      --green-bg2: #e4ede4;
      --accent: #3d5a3e;
      --white: #ffffff;
      --text-main: #1a2b1a;
      --text-muted: #6b7e6b;
      --border: #d4e4d4;
      --error: #c0392b;
      --gold: #8B6914;
      --gold-pale: #f5f0e8;
    }

    .bs-section {
      padding: 100px 24px;
      background: linear-gradient(150deg, var(--green-bg) 0%, var(--green-bg2) 100%);
      font-family: 'DM Sans', sans-serif;
      min-height: 100vh;
    }
    .bs-inner { max-width: 760px; margin: 0 auto; }

    .bs-header { text-align: center; margin-bottom: 52px; }
    .bs-eyebrow {
      display: inline-block;
      background: var(--green-mid); color: #fff;
      font-size: 10px; font-weight: 600; letter-spacing: 3.5px; text-transform: uppercase;
      padding: 6px 20px; border-radius: 40px; margin-bottom: 20px;
    }
    .bs-title {
      font-family: 'Playfair Display', serif;
      font-size: clamp(36px, 5vw, 54px); font-weight: 700;
      color: var(--green-dark); margin: 0 0 14px; line-height: 1.15;
    }
    .bs-subtitle { font-size: 16px; color: var(--text-muted); font-weight: 300; }

    .bs-card {
      background: var(--white);
      border-radius: 28px;
      box-shadow: 0 24px 80px rgba(61,90,62,0.14), 0 4px 24px rgba(61,90,62,0.06);
      overflow: hidden;
    }

    .bs-card-head {
      background: linear-gradient(135deg, var(--green-mid) 0%, #2d4230 100%);
      padding: 28px 36px;
      display: flex; align-items: flex-start; justify-content: space-between;
      position: sticky; top: 0; z-index: 5;
    }
    .bs-card-title {
      font-family: 'Playfair Display', serif;
      font-size: 20px; color: #fff; margin: 0 0 8px;
    }
    .bs-contacts { display: flex; gap: 16px; flex-wrap: wrap; }
    .bs-contact-link {
      display: flex; align-items: center; gap: 6px;
      color: rgba(255,255,255,0.75); font-size: 13px; text-decoration: none;
      transition: color 0.2s;
    }
    .bs-contact-link:hover { color: var(--green-pale); }
    .bs-close-btn {
      background: rgba(255,255,255,0.15); border: none; color: #fff;
      width: 36px; height: 36px; border-radius: 50%;
      font-size: 16px; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: background 0.2s; flex-shrink: 0;
    }
    .bs-close-btn:hover { background: rgba(255,255,255,0.3); }

    .bs-selected-room-banner {
      display: flex; align-items: center; justify-content: space-between;
      background: linear-gradient(135deg, #edf5ed, #dff0df);
      border-bottom: 2px solid var(--green-pale);
      padding: 14px 36px; gap: 12px; flex-wrap: wrap;
    }
    .bs-selected-room-banner-left { display: flex; align-items: center; gap: 10px; }
    .bs-selected-room-banner-img {
      width: 52px; height: 52px; border-radius: 10px;
      object-fit: cover; border: 2px solid var(--green-pale);
    }
    .bs-selected-room-banner-name {
      font-family: 'Playfair Display', serif;
      font-size: 16px; font-weight: 700; color: var(--green-dark);
    }
    .bs-selected-room-banner-meta { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
    .bs-selected-room-banner-price {
      font-size: 22px; font-weight: 700; color: var(--green-mid);
      font-family: 'Playfair Display', serif;
    }
    .bs-selected-room-banner-price span { font-size: 12px; font-weight: 400; font-family: 'DM Sans', sans-serif; color: var(--text-muted); }

    .bs-body { padding: 36px; }

    .bs-seg-label {
      font-size: 11px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase;
      color: var(--green-light); margin: 0 0 16px; padding-bottom: 10px;
      border-bottom: 1.5px solid var(--green-bg2);
    }
    .bs-seg-label + .bs-seg-label,
    .bs-row + .bs-seg-label { margin-top: 24px; }

    .bs-row { display: grid; gap: 14px; margin-bottom: 14px; }
    .bs-row.cols-2 { grid-template-columns: 1fr 1fr; }

    .bs-field { display: flex; flex-direction: column; gap: 5px; }
    .bs-label { font-size: 11px; font-weight: 600; color: var(--text-main); letter-spacing: 0.3px; }
    .bs-req { color: var(--error); margin-left: 2px; }

    .bs-input, .bs-select, .bs-textarea {
      width: 100%; padding: 11px 18px;
      border: 1.5px solid var(--border); border-radius: 50px;
      font-family: 'DM Sans', sans-serif; font-size: 14px; color: var(--text-main);
      background: #fafcfa;
      transition: border-color 0.2s, box-shadow 0.2s;
      box-sizing: border-box; outline: none;
    }
    .bs-textarea { border-radius: 18px; resize: vertical; min-height: 84px; }
    .bs-input:focus, .bs-select:focus, .bs-textarea:focus {
      border-color: var(--green-mid);
      box-shadow: 0 0 0 3px rgba(61,90,62,0.1);
      background: #fff;
    }
    .bs-input.err, .bs-select.err { border-color: var(--error); }
    .bs-err-msg { font-size: 11px; color: var(--error); padding-left: 6px; }

    /* ── Room Cards ── */
    .bs-room-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 14px; }
    .bs-room-card {
      border: 2px solid var(--border); border-radius: 16px;
      padding: 14px 16px; cursor: pointer;
      background: #fafcfa;
      transition: all 0.2s;
      display: flex; flex-direction: column; gap: 6px;
    }
    .bs-room-card:hover { border-color: var(--green-light); background: #f5faf5; }
    .bs-room-card.active {
      border-color: var(--green-mid); background: #edf5ed;
      box-shadow: 0 0 0 3px rgba(61,90,62,0.08);
    }
    /* Suite card gets gold accent when active */
    .bs-room-card.suite.active {
      border-color: var(--gold); background: var(--gold-pale);
      box-shadow: 0 0 0 3px rgba(139,105,20,0.1);
    }
    .bs-room-card.suite.active .bs-room-check {
      background: var(--gold); border-color: var(--gold); color: #fff;
    }
    .bs-room-card.suite.active .bs-room-price { color: var(--gold); }

    .bs-room-card-top { display: flex; align-items: center; justify-content: space-between; }
    .bs-room-badge {
      font-size: 10px; font-weight: 700; letter-spacing: 0.5px;
      padding: 3px 10px; border-radius: 20px; color: #fff;
    }
    .bs-room-check {
      width: 20px; height: 20px; border-radius: 50%;
      border: 2px solid var(--border);
      background: #fff;
      display: flex; align-items: center; justify-content: center;
      font-size: 10px; transition: all 0.2s;
    }
    .bs-room-card.active .bs-room-check {
      background: var(--green-mid); border-color: var(--green-mid); color: #fff;
    }
    .bs-room-name { font-size: 13px; font-weight: 600; color: var(--text-main); line-height: 1.3; }
    .bs-room-price { font-size: 15px; font-weight: 700; color: var(--green-mid); }
    .bs-room-price span { font-size: 11px; font-weight: 400; color: var(--text-muted); }
    .bs-room-guests { font-size: 11px; color: var(--text-muted); }

    /* Amenity pills inside room card */
    .bs-room-amenities {
      display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px;
    }
    .bs-room-amenity-pill {
      font-size: 10px; padding: 2px 8px;
      border-radius: 20px; background: var(--green-bg2);
      color: var(--text-muted); white-space: nowrap;
    }
    .bs-room-card.suite .bs-room-amenity-pill { background: #f0e8d0; color: #7a5c10; }

    .bs-guest-row {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 16px;
      background: #f5faf5; border: 1.5px solid var(--border);
      border-radius: 50px; margin-bottom: 14px;
    }
    .bs-guest-row label { font-size: 12px; font-weight: 600; color: var(--text-main); flex: 1; }
    .bs-guest-row input {
      width: 70px; padding: 6px 10px; border: 1.5px solid var(--border);
      border-radius: 30px; font-family: 'DM Sans', sans-serif;
      font-size: 13px; text-align: center; outline: none;
      transition: border-color 0.2s;
    }
    .bs-guest-row input:focus { border-color: var(--green-mid); }
    .bs-guest-total { font-size: 12px; color: var(--text-muted); white-space: nowrap; }
    .bs-guest-total strong { color: var(--green-mid); }

    /* Suite price box gets gold tones */
    .bs-price-box {
      background: linear-gradient(135deg, #f5faf5 0%, #edf5ed 100%);
      border: 1.5px solid var(--green-pale);
      border-radius: 18px; padding: 18px 22px; margin: 20px 0;
      display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px;
      transition: background 0.3s, border-color 0.3s;
    }
    .bs-price-box.suite {
      background: linear-gradient(135deg, #fdf8ef 0%, #f5edd8 100%);
      border-color: #d4b875;
    }
    .bs-price-box.suite .bs-price-amount,
    .bs-price-box.suite .bs-price-night { color: var(--gold); }

    .bs-price-label { font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
    .bs-price-detail { font-size: 12px; color: var(--text-muted); }
    .bs-price-amount {
      font-family: 'Playfair Display', serif; font-size: 34px;
      color: var(--green-mid); font-weight: 700; line-height: 1;
    }
    .bs-price-night { font-size: 18px; color: var(--green-mid); font-weight: 700; }

    .bs-submit-btn {
      width: 100%; background: var(--green-mid); color: #fff; border: none;
      padding: 15px; border-radius: 50px;
      font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 600;
      letter-spacing: 0.5px; cursor: pointer;
      transition: all 0.3s; margin-bottom: 12px;
    }
    .bs-submit-btn:hover {
      background: var(--green-dark); transform: translateY(-2px);
      box-shadow: 0 10px 36px rgba(61,90,62,0.32);
    }
    .bs-submit-btn.suite { background: var(--gold); }
    .bs-submit-btn.suite:hover {
      background: #6b4e10;
      box-shadow: 0 10px 36px rgba(139,105,20,0.32);
    }

    .bs-success { text-align: center; padding: 60px 36px; }
    .bs-success-icon { font-size: 64px; margin-bottom: 16px; }
    .bs-success-title {
      font-family: 'Playfair Display', serif; font-size: 28px;
      color: var(--green-mid); margin: 0 0 12px;
    }
    .bs-success-msg { font-size: 15px; color: var(--text-muted); line-height: 1.7; }
    .bs-success-cta { margin-top: 24px; }
    .bs-success-cta a {
      color: var(--green-mid); font-weight: 600; font-size: 17px; text-decoration: none;
    }

    .bs-overlay {
      position: fixed; inset: 0; z-index: 1100;
      background: rgba(10, 20, 10, 0.65);
      backdrop-filter: blur(6px);
      display: flex; align-items: center; justify-content: center;
      padding: 16px;
      opacity: 0; pointer-events: none;
      transition: opacity 0.3s ease;
    }
    .bs-overlay.open { opacity: 1; pointer-events: all; }
    .bs-modal-wrap {
      width: 100%; max-width: 760px; max-height: 92vh;
      overflow-y: auto; position: relative; border-radius: 28px;
      box-shadow: 0 40px 120px rgba(0,0,0,0.3);
      transform: translateY(28px) scale(0.97); opacity: 0;
      transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease;
    }
    .bs-overlay.open .bs-modal-wrap { transform: translateY(0) scale(1); opacity: 1; }

    @media (max-width: 600px) {
      .bs-room-grid { grid-template-columns: 1fr; }
      .bs-row.cols-2 { grid-template-columns: 1fr; }
      .bs-body { padding: 24px; }
      .bs-card-head { padding: 20px 24px; }
      .bs-selected-room-banner { padding: 12px 24px; }
      .bs-modal-wrap { max-height: 96vh; }
      .bs-success { padding: 40px 24px; }
    }
  `;

  // Detect if selected room is a Suite (id: 2)
  const isSuite = selected?.id === 2;

  const Card = (
    <div className="bs-card">
      {/* Card Header */}
      <div className="bs-card-head">
        <div>
          <h3 className="bs-card-title">Make a Reservation</h3>
          <div className="bs-contacts">
            <a href="tel:+919404617122" className="bs-contact-link">📞 9404617122</a>
            <a href="tel:+919404992515" className="bs-contact-link">📞 9404992515</a>
          </div>
        </div>
        {isModal && (
          <button className="bs-close-btn" onClick={onClose} aria-label="Close">✕</button>
        )}
      </div>

      {/* Selected Room Banner */}
      {selectedRoom && (
        <div className="bs-selected-room-banner">
          <div className="bs-selected-room-banner-left">
            {selectedRoom.image && (
              <img
                src={selectedRoom.image}
                alt={selectedRoom.name}
                className="bs-selected-room-banner-img"
              />
            )}
            <div>
              <div className="bs-selected-room-banner-name">{selectedRoom.name}</div>
              <div className="bs-selected-room-banner-meta">
                👥 Up to {selectedRoom.maxGuests} guests &nbsp;·&nbsp;
                <span style={{ color: selectedRoom.badgeColor, fontWeight: 600 }}>
                  {selectedRoom.badge}
                </span>
              </div>
            </div>
          </div>
          <div className="bs-selected-room-banner-price">
            ₹{selectedRoom.price.toLocaleString()} <span>/night</span>
          </div>
        </div>
      )}

      {/* Success State */}
      {submitted ? (
        <div className="bs-success">
          <div className="bs-success-icon">🎉</div>
          <h3 className="bs-success-title">Booking Request Sent!</h3>
          <p className="bs-success-msg">
            Thank you! We've received your booking request.<br />
            We'll call you within 1 hour to confirm your stay at Shrileela Beach Resort.
          </p>
          <div className="bs-success-cta">
            <a href="tel:+919404617122">📞 Call us: +91 9404617122</a>
          </div>
        </div>
      ) : (
        <div className="bs-body">

          {/* ── Stay Details ── */}
          <div className="bs-seg-label">📅 Stay Details</div>
          <div className="bs-row cols-2">
            <div className="bs-field">
              <label className="bs-label">Check-in Date <span className="bs-req">*</span></label>
              <input type="date" className="bs-input"
                value={form.checkIn} min={today}
                onChange={e => {
                  set("checkIn", e.target.value);
                  if (form.checkOut && e.target.value >= form.checkOut) set("checkOut", "");
                }} />
            </div>
            <div className="bs-field">
              <label className="bs-label">Check-out Date <span className="bs-req">*</span></label>
              <input type="date" className={`bs-input${errors.checkOut ? " err" : ""}`}
                value={form.checkOut} min={form.checkIn || today}
                onChange={e => set("checkOut", e.target.value)} />
              {errors.checkOut && <span className="bs-err-msg">{errors.checkOut}</span>}
            </div>
          </div>

          {/* ── Room Selection ── */}
          <div className="bs-seg-label">🏨 Select Room</div>

          <div className="bs-room-grid">
            {roomTypes.map(room => {
              const isSuiteCard = room.id === 2;
              return (
                <div
                  key={room.id}
                  className={`bs-room-card${isSuiteCard ? " suite" : ""}${form.roomId === room.id ? " active" : ""}`}
                  onClick={() => set("roomId", room.id)}
                >
                  <div className="bs-room-card-top">
                    <span
                      className="bs-room-badge"
                      style={{ background: room.badgeColor }}
                    >{room.badge}</span>
                    <div className="bs-room-check">
                      {form.roomId === room.id ? "✓" : ""}
                    </div>
                  </div>
                  <div className="bs-room-name">{room.name}</div>
                  <div className="bs-room-price">
                    ₹{room.price.toLocaleString()} <span>/night</span>
                  </div>
                  <div className="bs-room-guests">👥 Up to {room.maxGuests} guests</div>
                  {/* Amenity pills */}
                  {room.amenities && (
                    <div className="bs-room-amenities">
                      {room.amenities.slice(0, 3).map((a, i) => (
                        <span key={i} className="bs-room-amenity-pill">{a}</span>
                      ))}
                      {room.amenities.length > 3 && (
                        <span className="bs-room-amenity-pill">+{room.amenities.length - 3} more</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Extra Guests */}
          <div className="bs-guest-row">
            <label>
              Extra Guests{" "}
              <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
                (above {defaultGuests})
              </span>
            </label>
            <input
              type="number" min={0}
              value={form.extraGuests}
              onChange={e => set("extraGuests", e.target.value)}
            />
            <span className="bs-guest-total">Total: <strong>{totalGuests}</strong></span>
          </div>

          {/* Price Preview */}
          {pricePerNight > 0 && (
            <div className={`bs-price-box${isSuite ? " suite" : ""}`}>
              <div>
                <div className="bs-price-label">Estimated Total</div>
                {total ? (
                  <div className="bs-price-detail">
                    {nights} night{nights > 1 ? "s" : ""} × ₹{pricePerNight.toLocaleString()}
                  </div>
                ) : (
                  <div className="bs-price-detail">Select dates to see total</div>
                )}
              </div>
              <div>
                {total ? (
                  <div className="bs-price-amount">₹{total.toLocaleString()}</div>
                ) : (
                  <div className="bs-price-night">
                    ₹{pricePerNight.toLocaleString()}
                    <span style={{ fontSize: "14px", fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}>/night</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Your Info ── */}
          <div className="bs-seg-label">👤 Your Information</div>

          <div className="bs-row cols-2">
            <div className="bs-field">
              <label className="bs-label">Full Name <span className="bs-req">*</span></label>
              <input type="text" className={`bs-input${errors.name ? " err" : ""}`}
                placeholder="Your full name"
                value={form.name} onChange={e => set("name", e.target.value)} />
              {errors.name && <span className="bs-err-msg">{errors.name}</span>}
            </div>
            <div className="bs-field">
              <label className="bs-label">Phone <span className="bs-req">*</span></label>
              <input type="tel" className={`bs-input${errors.phone ? " err" : ""}`}
                placeholder="+91 XXXXX XXXXX"
                value={form.phone} onChange={e => set("phone", e.target.value)} />
              {errors.phone && <span className="bs-err-msg">{errors.phone}</span>}
            </div>
          </div>

          <div className="bs-row" style={{ marginBottom: "14px" }}>
            <div className="bs-field">
              <label className="bs-label">Email (Optional)</label>
              <input type="email" className={`bs-input${errors.email ? " err" : ""}`}
                placeholder="your@email.com"
                value={form.email} onChange={e => set("email", e.target.value)} />
              {errors.email && <span className="bs-err-msg">{errors.email}</span>}
            </div>
          </div>

          <div className="bs-field" style={{ marginBottom: "28px" }}>
            <label className="bs-label">Special Requests (Optional)</label>
            <textarea className="bs-textarea"
              placeholder="Any special requests or requirements..."
              value={form.requests} onChange={e => set("requests", e.target.value)} />
          </div>

          <button
            type="button"
            className={`bs-submit-btn${isSuite ? " suite" : ""}`}
            onClick={handleSubmit}
          >
            {isSuite ? "👑 Confirm Suite Booking" : "✓ Confirm Booking Request"}
          </button>
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <>
        <style>{styles}</style>
        <div
          className={`bs-overlay${isOpen ? " open" : ""}`}
          onClick={e => { if (e.target.classList.contains("bs-overlay")) onClose?.(); }}
        >
          <div className="bs-modal-wrap">{Card}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <section id="booking" className="bs-section">
        <div className="bs-inner">
          <div className="bs-header">
            <span className="bs-eyebrow">Reserve Now</span>
            <h2 className="bs-title">Book Your Stay</h2>
            <p className="bs-subtitle">Select your dates and enjoy a relaxing beach getaway</p>
          </div>
          {Card}
        </div>
      </section>
    </>
  );
};

export default BookingSection;